import React, { useState, useEffect, useRef } from 'react';
import { NavLink, Link, useLocation } from 'react-router-dom';
import { useAuth } from '../../hooks/useAuth';
import { useTheme } from '../../hooks/useTheme';

const Navbar: React.FC = () => {
  const location = useLocation();
  const [isScrolled, setIsScrolled] = useState(false);
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);
  const [isProfileDropdownOpen, setIsProfileDropdownOpen] = useState(false);
  const { user, logout } = useAuth();
  const { toggleTheme } = useTheme();
  const profileDropdownRef = useRef<HTMLDivElement>(null);

  // SSS, Login, Register, Admin ve Account sayfalarında navbar'ı her zaman görünür yap
  const isAlwaysScrolledPage =
    location.pathname === '/faq' ||
    location.pathname === '/login' ||
    location.pathname === '/register' ||
    location.pathname.startsWith('/admin') ||
    location.pathname === '/account';

  // Navbar şeffaf → opak efekti (belirli sayfalarda devre dışı)
  useEffect(() => {
    if (isAlwaysScrolledPage) {
      setIsScrolled(true); // Bu sayfalarda her zaman scrolled durumunda
      return;
    }

    const onScroll = () => setIsScrolled(window.scrollY > 50);
    onScroll();
    window.addEventListener('scroll', onScroll, { passive: true });
    return () => window.removeEventListener('scroll', onScroll);
  }, [isAlwaysScrolledPage]);

  // Mobil menü açıldığında body scroll'u kapat
  useEffect(() => {
    if (isMobileMenuOpen) {
      document.body.style.overflow = 'hidden';
    } else {
      document.body.style.overflow = 'unset';
    }
    return () => {
      document.body.style.overflow = 'unset';
    };
  }, [isMobileMenuOpen]);

  // Profil dropdown dışına tıklandığında kapat
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (profileDropdownRef.current && !profileDropdownRef.current.contains(event.target as Node)) {
        setIsProfileDropdownOpen(false);
      }
    };

    if (isProfileDropdownOpen) {
      document.addEventListener('mousedown', handleClickOutside);
    }

    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [isProfileDropdownOpen]);

  const handleLogout = async () => {
    await logout();
    window.location.href = '/';
  };

  return (
    <nav className={`navbar ${isScrolled ? 'scrolled' : ''} ${isMobileMenuOpen ? 'mobile-menu-open' : ''}`}>
      {/* Sol - Logo */}
      <div className="navbar-left">
        <img src="/gorseller/sk-logo.png" alt="SK Logo" className="navbar-logo" />
      </div>

      {/* Hamburger Menu Button - Sadece mobilde görünür */}
      <button
        className="hamburger-btn"
        onClick={() => setIsMobileMenuOpen(!isMobileMenuOpen)}
        aria-label="Menüyü aç/kapat"
      >
        <span className={isMobileMenuOpen ? 'open' : ''}></span>
        <span className={isMobileMenuOpen ? 'open' : ''}></span>
        <span className={isMobileMenuOpen ? 'open' : ''}></span>
      </button>

      {/* Orta - Linkler (Masaüstünde görünür) */}
      <div className="navbar-center navbar-center-desktop">
        <NavLink to="/" end className={({ isActive }) => `nav-btn ${isActive ? 'active' : ''}`}>
          Hakkımızda
        </NavLink>
        <NavLink to="/calisma-alanlarimiz" className={({ isActive }) => `nav-btn ${isActive ? 'active' : ''}`}>
          Çalışma alanlarımız
        </NavLink>
        <NavLink to="/announcements" className={({ isActive }) => `nav-btn ${isActive ? 'active' : ''}`}>
          Duyurular
        </NavLink>
        <NavLink to="/faq" className={({ isActive }) => `nav-btn ${isActive ? 'active' : ''}`}>
          SSS
        </NavLink>
      </div>

      {/* Sağ - Kullanıcı & Tema (Masaüstünde görünür) */}
      <div className="navbar-right navbar-right-desktop">
        {user ? (
          <div className="user-menu">
            <div className="profile-dropdown-container" ref={profileDropdownRef}>
              <button
                type="button"
                className="user-name"
                onClick={() => setIsProfileDropdownOpen(!isProfileDropdownOpen)}
              >
                <i className="bi bi-person-circle me-2"></i>
                {user.name}
              </button>

              {isProfileDropdownOpen && (
                <div className="profile-dropdown-menu">
                  <div className="profile-dropdown-header">
                    <div className="profile-dropdown-avatar">
                      <i className="bi bi-person-circle"></i>
                    </div>
                    <div className="profile-dropdown-info">
                      <span className="profile-dropdown-name">{user.name}</span>
                      <span className="profile-dropdown-email">{user.email}</span>
                    </div>
                  </div>
                  <div className="profile-dropdown-divider"></div>
                  <Link
                    to="/account"
                    className="profile-dropdown-item"
                    onClick={() => setIsProfileDropdownOpen(false)}
                  >
                    <i className="bi bi-gear"></i>
                    <span>Hesap Ayarları</span>
                  </Link>
                  {(user.role?.role_name === 'admin' || user.role?.role_name === 'lawyer') && (
                    <Link
                      to="/admin"
                      className="profile-dropdown-item"
                      onClick={() => setIsProfileDropdownOpen(false)}
                    >
                      <i className="bi bi-speedometer2"></i>
                      <span>Admin Panel</span>
                    </Link>
                  )}
                  <div className="profile-dropdown-divider"></div>
                  <button
                    type="button"
                    className="profile-dropdown-item logout"
                    onClick={() => {
                      setIsProfileDropdownOpen(false);
                      handleLogout();
                    }}
                  >
                    <i className="bi bi-box-arrow-right"></i>
                    <span>Çıkış Yap</span>
                  </button>
                </div>
              )}
            </div>
          </div>
        ) : (
          <div className="auth-buttons">
            <Link to="/login" className="btn btn-sm btn-login">
              <i className="bi bi-box-arrow-in-right me-1"></i>
              Giriş
            </Link>
            <Link to="/register" className="btn btn-sm btn-register">
              <i className="bi bi-person-plus me-1"></i>
              Kayıt
            </Link>
          </div>
        )}
        <button
          type="button"
          className="theme-toggle-btn"
          onClick={toggleTheme}
          aria-label="Tema Değiştir"
        >
          <i className="bi bi-sun-fill"></i>
          <i className="bi bi-moon-stars-fill"></i>
        </button>
      </div>

      {/* Mobile Menu (Sadece mobilde görünür) */}
      <div className={`mobile-menu ${isMobileMenuOpen ? 'mobile-menu-open' : ''}`}>
        <div className="mobile-menu-content">
          {/* Navigation Links */}
          <div className="mobile-menu-section">
            <NavLink
              to="/"
              end
              className={({ isActive }) => `mobile-menu-item ${isActive ? 'active' : ''}`}
              onClick={() => setIsMobileMenuOpen(false)}
            >
              <i className="bi bi-house-door"></i>
              <span>Hakkımızda</span>
            </NavLink>
            <NavLink
              to="/calisma-alanlarimiz"
              className={({ isActive }) => `mobile-menu-item ${isActive ? 'active' : ''}`}
              onClick={() => setIsMobileMenuOpen(false)}
            >
              <i className="bi bi-briefcase"></i>
              <span>Çalışma Alanlarımız</span>
            </NavLink>
            <NavLink
              to="/announcements"
              className={({ isActive }) => `mobile-menu-item ${isActive ? 'active' : ''}`}
              onClick={() => setIsMobileMenuOpen(false)}
            >
              <i className="bi bi-megaphone"></i>
              <span>Duyurular</span>
            </NavLink>
            <NavLink
              to="/faq"
              className={({ isActive }) => `mobile-menu-item ${isActive ? 'active' : ''}`}
              onClick={() => setIsMobileMenuOpen(false)}
            >
              <i className="bi bi-question-circle"></i>
              <span>SSS</span>
            </NavLink>
          </div>

          <div className="mobile-menu-divider"></div>

          {/* User Section */}
          {user ? (
            <div className="mobile-menu-section mobile-user-section">
              <div className="mobile-user-info">
                <i className="bi bi-person-circle"></i>
                <div className="mobile-user-details">
                  <span className="mobile-user-name">{user.name}</span>
                  <span className="mobile-user-email">{user.email}</span>
                </div>
              </div>

              <Link
                to="/account"
                className="mobile-menu-item"
                onClick={() => setIsMobileMenuOpen(false)}
              >
                <i className="bi bi-gear"></i>
                <span>Hesap Ayarları</span>
              </Link>

              {(user.role?.role_name === 'admin' || user.role?.role_name === 'lawyer') && (
                <Link
                  to="/admin"
                  className="mobile-menu-item"
                  onClick={() => setIsMobileMenuOpen(false)}
                >
                  <i className="bi bi-speedometer2"></i>
                  <span>Admin Panel</span>
                </Link>
              )}

              <button
                type="button"
                className="mobile-menu-item mobile-logout"
                onClick={() => {
                  setIsMobileMenuOpen(false);
                  handleLogout();
                }}
              >
                <i className="bi bi-box-arrow-right"></i>
                <span>Çıkış Yap</span>
              </button>
            </div>
          ) : (
            <div className="mobile-menu-section mobile-auth-section">
              <Link
                to="/login"
                className="mobile-menu-btn mobile-login-btn"
                onClick={() => setIsMobileMenuOpen(false)}
              >
                <i className="bi bi-box-arrow-in-right"></i>
                <span>Giriş Yap</span>
              </Link>
              <Link
                to="/register"
                className="mobile-menu-btn mobile-register-btn"
                onClick={() => setIsMobileMenuOpen(false)}
              >
                <i className="bi bi-person-plus"></i>
                <span>Kayıt Ol</span>
              </Link>
            </div>
          )}

          <div className="mobile-menu-divider"></div>

          {/* Theme Toggle */}
          <div className="mobile-menu-section">
            <button
              type="button"
              className="mobile-menu-item mobile-theme-toggle"
              onClick={toggleTheme}
            >
              <i className="bi bi-sun-fill"></i>
              <i className="bi bi-moon-stars-fill"></i>
              <span>Tema Değiştir</span>
            </button>
          </div>
        </div>
      </div>
    </nav>
  );
};

export default Navbar;
